import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface MonthlyWeatherData {
  location_id: string;
  user_id: string;
  month: number;
  temperature_avg: number;
  temperature_min: number;
  temperature_max: number;
  precipitation_avg: number;
  humidity_avg: number;
  cloud_cover_avg: number;
  wind_speed_avg: number;
  pressure_avg?: number;
  data_source: string;
  years_included?: number[];
  years_count?: number;
  data_quality_score?: number;
}

interface UseMonthlyWeatherProps {
  locationId: string;
  month: number;
  year: number;
  enabled?: boolean;
}

export const useMonthlyWeather = ({ locationId, month, year, enabled = true }: UseMonthlyWeatherProps) => {
  const [weatherData, setWeatherData] = useState<MonthlyWeatherData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  const fetchWeatherData = useCallback(async () => {
    if (!enabled || !user || !locationId) return;

    setLoading(true);
    setError(null);

    try {
      // Get location coordinates
      const { data: locationData } = await supabase
        .from('favorite_locations')
        .select('latitude, longitude')
        .eq('id', locationId)
        .single();

      if (!locationData) {
        throw new Error('Location not found');
      }

      // Call edge function — it handles caching internally
      const { data: apiData, error: apiError } = await supabase.functions.invoke('get-historical-weather', {
        body: {
          locationId,
          latitude: locationData.latitude,
          longitude: locationData.longitude,
          userId: user.id,
          specificMonth: month + 1, // Convert 0-11 to 1-12
        }
      });

      if (apiError) throw apiError;

      // Handle edge function error responses
      if (apiData?.error) {
        throw new Error(apiData.error);
      }

      // Find the data for our specific month
      const monthData = apiData?.data?.find((d: MonthlyWeatherData) => d.month === month + 1);

      if (monthData) {
        setWeatherData(monthData);
      } else {
        console.warn('Weather response:', JSON.stringify(apiData));
        setError('No weather data available for this month');
      }

    } catch (err) {
      console.error('Error fetching weather data:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch weather data');
    } finally {
      setLoading(false);
    }
  }, [locationId, month, year, user, enabled]);

  useEffect(() => {
    if (enabled) {
      fetchWeatherData();
    }
  }, [fetchWeatherData, enabled]);

  const retry = useCallback(() => {
    fetchWeatherData();
  }, [fetchWeatherData]);

  return {
    data: weatherData,
    loading,
    error,
    retry
  };
};